import { createClient } from '@supabase/supabase-js';
import { supabaseClientOptions } from './supabaseClientOptions';

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error('Missing Supabase environment variables. Set REACT_APP_SUPABASE_URL and REACT_APP_SUPABASE_ANON_KEY.');
}

export const supabase = createClient(supabaseUrl, supabaseAnonKey, supabaseClientOptions);

// Auth helpers
export const auth = {
  // Sign up with email and password
  signUp: async ({ email, password, firstName, lastName, phone, metadata = {} }) => {
    const safeFirstName = (firstName || 'User').trim() || 'User';
    const safeLastName = (lastName || '').trim();
    const safePhone = (phone || '').trim();

    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          first_name: safeFirstName,
          last_name: safeLastName,
          firstName: safeFirstName,
          lastName: safeLastName,
          full_name: [safeFirstName, safeLastName].filter(Boolean).join(' '),
          phone: safePhone,
          ...metadata,
        },
      },
    });
    return { data, error };
  },

  // Sign in with email and password
  signIn: async ({ email, password }) => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    return { data, error };
  },

  // Sign out
  signOut: async () => {
    const { error } = await supabase.auth.signOut();
    return { error };
  },

  // Get current user
  getUser: async () => {
    const { data: { user }, error } = await supabase.auth.getUser();
    return { user, error };
  },

  // Get session
  getSession: async () => {
    const { data: { session }, error } = await supabase.auth.getSession();
    return { session, error };
  },

  // Admin MFA helpers
  getMfaAssuranceLevel: async () => {
    return supabase.auth.mfa.getAuthenticatorAssuranceLevel();
  },

  listMfaFactors: async () => {
    return supabase.auth.mfa.listFactors();
  },

  enrollTotpFactor: async () => {
    return supabase.auth.mfa.enroll({ factorType: 'totp' });
  },

  verifyMfaCode: async ({ factorId, code }) => {
    return supabase.auth.mfa.challengeAndVerify({ factorId, code });
  },

  // Reset password
  resetPassword: async (email) => {
    const { data, error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
    });
    return { data, error };
  },

  // Update password
  updatePassword: async (newPassword) => {
    const { data, error } = await supabase.auth.updateUser({
      password: newPassword,
    });
    return { data, error };
  },

  // Listen to auth changes
  onAuthStateChange: (callback) => {
    return supabase.auth.onAuthStateChange(callback);
  },

  // Sign in with Google
  signInWithGoogle: async () => {
    const { data, error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: `${window.location.origin}/auth/callback`,
        queryParams: {
          access_type: 'offline',
          prompt: 'consent',
        },
      },
    });
    return { data, error };
  },

  // Sign in with Twitter
  signInWithTwitter: async () => {
    const { data, error } = await supabase.auth.signInWithOAuth({
      provider: 'twitter',
      options: {
        redirectTo: `${window.location.origin}/auth/callback`,
      },
    });
    return { data, error };
  },

  // Verify email with OTP (for signup confirmation)
  verifyOTP: async (email, token) => {
    const { data, error } = await supabase.auth.verifyOtp({
      email,
      token,
      type: 'signup', // Use 'signup' for email confirmation after registration
    });
    return { data, error };
  },

  // Resend verification email
  resendVerificationEmail: async (email) => {
    const { data, error } = await supabase.auth.resend({
      type: 'signup',
      email,
    });
    return { data, error };
  },
};

// Database helpers
export const db = {
  // Profiles
  profiles: {
    get: async (userId) => {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .single();
      return { data, error };
    },
    update: async (userId, updates) => {
      const { data, error } = await supabase
        .from('profiles')
        .update(updates)
        .eq('id', userId)
        .select()
        .single();
      return { data, error };
    },
  },

  // Products
  products: {
    getAll: async ({ category, featured, limit = 50 } = {}) => {
      let query = supabase
        .from('products')
        .select('*')
        .eq('is_active', true)
        .order('created_at', { ascending: false })
        .limit(limit);

      if (category) query = query.eq('category', category);
      if (featured) query = query.eq('is_featured', true);

      const { data, error } = await query;
      return { data, error };
    },
    getBySlug: async (slug) => {
      const { data, error } = await supabase
        .from('products')
        .select('*')
        .eq('slug', slug)
        .single();
      return { data, error };
    },
    getById: async (id) => {
      const { data, error } = await supabase
        .from('products')
        .select('*')
        .eq('id', id)
        .single();
      return { data, error };
    },
    search: async (query) => {
      const { data, error } = await supabase
        .from('products')
        .select('*')
        .eq('is_active', true)
        .or(`name.ilike.%${query}%,description.ilike.%${query}%,name_ar.ilike.%${query}%`)
        .limit(20);
      return { data, error };
    },
  },

  // Cart
  cart: {
    get: async (userId) => {
      const { data, error } = await supabase
        .from('carts')
        .select(`
          *,
          cart_items (
            *,
            product:products (*),
            variant:product_variants (*),
            design:designs (*)
          )
        `)
        .eq('user_id', userId)
        .single();
      return { data, error };
    },
    addItem: async (cartId, item) => {
      const { data, error } = await supabase
        .from('cart_items')
        .insert({
          cart_id: cartId,
          product_id: item.productId,
          variant_id: item.variantId || null,
          design_id: item.designId || null,
          quantity: item.quantity,
          unit_price: item.unitPrice,
          customizations: item.customizations || {},
        })
        .select()
        .single();
      return { data, error };
    },
    updateItem: async (itemId, updates) => {
      const { data, error } = await supabase
        .from('cart_items')
        .update(updates)
        .eq('id', itemId)
        .select()
        .single();
      return { data, error };
    },
    removeItem: async (itemId) => {
      const { error } = await supabase
        .from('cart_items')
        .delete()
        .eq('id', itemId);
      return { error };
    },
    clear: async (cartId) => {
      const { error } = await supabase
        .from('cart_items')
        .delete()
        .eq('cart_id', cartId);
      return { error };
    },
  },

  // Orders
  orders: {
    getAll: async (userId) => {
      const { data, error } = await supabase
        .from('orders')
        .select(`
          *,
          order_items (
            *,
            product:products (*),
            variant:product_variants (*),
            design:designs (*)
          )
        `)
        .eq('user_id', userId)
        .order('created_at', { ascending: false });
      return { data, error };
    },
    getById: async (orderId) => {
      const { data, error } = await supabase
        .from('orders')
        .select(`
          *,
          order_items (
            *,
            product:products (*),
            variant:product_variants (*),
            design:designs (*)
          )
        `)
        .eq('id', orderId)
        .single();
      return { data, error };
    },
    create: async (orderData) => {
      // Generate order number
      const orderNumber = `PD-${new Date().toISOString().slice(0,10).replace(/-/g,'')}-${Math.floor(Math.random() * 10000).toString().padStart(4, '0')}`;

      const { data, error } = await supabase
        .from('orders')
        .insert({
          ...orderData,
          order_number: orderNumber,
        })
        .select()
        .single();
      return { data, error };
    },
    addItems: async (items) => {
      const { data, error } = await supabase
        .from('order_items')
        .insert(items)
        .select();
      return { data, error };
    },
  },

  // Designs
  designs: {
    getAll: async (userId) => {
      const { data, error } = await supabase
        .from('designs')
        .select('*, product:products(*)')
        .eq('user_id', userId)
        .order('updated_at', { ascending: false });
      return { data, error };
    },
    getById: async (designId) => {
      const { data, error } = await supabase
        .from('designs')
        .select('*, product:products(*)')
        .eq('id', designId)
        .single();
      return { data, error };
    },
    create: async (designData) => {
      const { data, error } = await supabase
        .from('designs')
        .insert(designData)
        .select()
        .single();
      return { data, error };
    },
    update: async (designId, updates) => {
      const { data, error } = await supabase
        .from('designs')
        .update(updates)
        .eq('id', designId)
        .select()
        .single();
      return { data, error };
    },
    delete: async (designId) => {
      const { error } = await supabase
        .from('designs')
        .delete()
        .eq('id', designId);
      return { error };
    },
  },

  // Addresses
  addresses: {
    getAll: async (userId) => {
      const { data, error } = await supabase
        .from('addresses')
        .select('*')
        .eq('user_id', userId)
        .order('is_default', { ascending: false });
      return { data, error };
    },
    create: async (addressData) => {
      const { data, error } = await supabase
        .from('addresses')
        .insert(addressData)
        .select()
        .single();
      return { data, error };
    },
    update: async (addressId, updates) => {
      const { data, error } = await supabase
        .from('addresses')
        .update(updates)
        .eq('id', addressId)
        .select()
        .single();
      return { data, error };
    },
    delete: async (addressId) => {
      const { error } = await supabase
        .from('addresses')
        .delete()
        .eq('id', addressId);
      return { error };
    },
    setDefault: async (userId, addressId) => {
      // First, unset all defaults
      await supabase
        .from('addresses')
        .update({ is_default: false })
        .eq('user_id', userId);

      // Then set the new default
      const { data, error } = await supabase
        .from('addresses')
        .update({ is_default: true })
        .eq('id', addressId)
        .select()
        .single();
      return { data, error };
    },
  },

};

// Storage helpers
export const storage = {
  upload: async (bucket, path, file) => {
    const { data, error } = await supabase.storage
      .from(bucket)
      .upload(path, file);
    return { data, error };
  },
  getPublicUrl: (bucket, path) => {
    const { data } = supabase.storage
      .from(bucket)
      .getPublicUrl(path);
    return data.publicUrl;
  },
  delete: async (bucket, paths) => {
    const { data, error } = await supabase.storage
      .from(bucket)
      .remove(paths);
    return { data, error };
  },
};

export default supabase;
